import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { Box, Button, Typography } from '@material-ui/core';
import { toast } from 'react-toastify';
import useStyles from './styles';

import getValidationErrors, { IErrors } from '../../utils/getValidationErrors';

import CustomForm from '../../components/CustomForm';
import { form, initialData, schema } from './form';
import companyService from '../../services/companyService';
import { useGetCategories } from '../../hooks/data/categories';
import { useAuth } from '../../hooks/auth';
import { HOME_URL } from '../../shared/environments';
import LoadingButton from '../../components/LoadingButton';

interface SignUpFormData {
  name: string;
  whatsapp: string;
  category: string;
}

const Company: React.FC = () => {
  const classes = useStyles();

  const categoriesQuery = useGetCategories();

  const { user } = useAuth();

  const [errors, setErrors] = useState<IErrors>({});
  const [isLoading, setIsLoading] = useState(false);

  const [data, setData] = useState<SignUpFormData>(initialData);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setIsLoading(true);
      try {
        await schema.validate(data, {
          abortEarly: false,
        });

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        await companyService.update(user!.company!.id!, {
          company_name: data.name,
          cellphone: data.whatsapp,
          category: [data.category],
        });

        toast.success('Dados atualizado com sucesso');
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errorsValidate = getValidationErrors(err);

          setErrors(errorsValidate);

          return;
        }

        toast.error(
          'Ocorreu ao realizar ao atualizar os dados. Tente novamente',
        );
      } finally {
        setIsLoading(false);
      }
    },
    [data, user],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setData({
        ...data,
        [e.target.name]:
          e.target.name === 'whatsapp'
            ? e.target.value.replace(/\D+/g, '')
            : e.target.value,
      });
      setErrors({
        ...errors,
        [e.target.name]: '',
      });
    },
    [data, errors],
  );

  const categoriesOptions = useMemo(() => {
    if (categoriesQuery.status !== 'success') {
      return [];
    }

    return categoriesQuery.data.map(category => ({
      value: category.id,
      label: category.name,
    }));
  }, [categoriesQuery.data, categoriesQuery.status]);

  useEffect(() => {
    setData({
      name: user?.company?.company_name ?? '',
      whatsapp: user?.company?.cellphone ?? '',
      category: user?.company?.category[0] ?? '',
    });
  }, [user]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      alignItems="center"
      textAlign="center"
      className={classes.root}
    >
      <Typography variant="h5" className={classes.title}>
        Dados do meu negócio
      </Typography>
      <form onSubmit={handleSubmit}>
        <CustomForm
          onChange={handleChange}
          data={data}
          form={form.map(formSection => ({
            ...formSection,
            items: formSection.items.map(formItem => {
              if (formItem.name !== 'category') {
                return formItem;
              }

              return {
                ...formItem,
                options: categoriesOptions,
              };
            }),
          }))}
          errors={errors}
        />
        <LoadingButton
          loading={isLoading}
          variant="contained"
          color="primary"
          className={classes.submitButton}
          type="submit"
          size="large"
          fullWidth
        >
          Atualizar
        </LoadingButton>
      </form>
      <Box marginTop={3} maxWidth="100%">
        <Typography>
          Copie o link abaixo para compartilhar no seu whataspp business ou nas
          suas redes sociais.
        </Typography>
        <Box
          color="primary"
          border="1px solid #ccc"
          borderRadius={5}
          padding={2}
          marginY={2}
          style={{ overflowWrap: 'break-word' }}
        >
          {`${HOME_URL}/parceiro/${user?.company?.slug}`}
        </Box>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            navigator.clipboard.writeText(
              `${HOME_URL}/parceiro/${user?.company?.slug}`,
            );
          }}
        >
          Copiar
        </Button>
      </Box>
    </Box>
  );
};

export default Company;
